import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { FullCrudPage } from "@/components/admin/FullCrudPage";
import { TextField } from "@/components/admin/fields";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { createGroup, deleteGroup, getGroup, listGroups, updateGroup } from "@/services/api/groups";
import { listPermissions } from "@/services/api/permissions";
import type { Group } from "@/services/api/types";

interface GroupForm {
  code: string;
  name: string;
  description: string;
  permission_ids: number[];
}

const emptyForm: GroupForm = { code: "", name: "", description: "", permission_ids: [] };

export function GroupsPage() {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const limit = 25;
  const query = useQuery({
    queryKey: ["groups", offset],
    queryFn: () => listGroups({ limit, offset }),
  });
  const permissions = useQuery({
    queryKey: ["permissions", "all"],
    queryFn: () => listPermissions({ limit: 500 }),
  });

  const columns: ColumnDef<Group>[] = [
    { accessorKey: "code", header: t("admin.code") },
    { accessorKey: "name", header: t("admin.name") },
    {
      accessorKey: "description",
      header: t("admin.description"),
      cell: ({ getValue }) => String(getValue() ?? "—"),
    },
    {
      accessorKey: "permission_ids",
      header: t("admin.permissions"),
      cell: ({ getValue }) => (getValue() as number[]).length,
    },
  ];

  const permOptions = (permissions.data?.items ?? []).map((p) => ({
    value: p.id,
    label: p.code,
  }));

  return (
    <FullCrudPage<Group, GroupForm>
      title={t("nav.groups")}
      entityType="group"
      getRecord={getGroup}
      columns={columns}
      data={query.data?.items ?? []}
      total={query.data?.total ?? 0}
      isLoading={query.isLoading}
      limit={limit}
      offset={offset}
      onPageChange={setOffset}
      queryKey="groups"
      emptyForm={emptyForm}
      toForm={(r) => ({
        code: r.code,
        name: r.name,
        description: r.description ?? "",
        permission_ids: r.permission_ids ?? [],
      })}
      canSubmit={(f) => Boolean(f.code && f.name)}
      renderForm={(form, setForm) => (
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label={t("admin.code")}
            value={form.code}
            onChange={(v) => setForm({ ...form, code: v })}
            required
          />
          <TextField
            label={t("admin.name")}
            value={form.name}
            onChange={(v) => setForm({ ...form, name: v })}
            required
          />
          <div className="col-span-2">
            <TextField
              label={t("admin.description")}
              value={form.description}
              onChange={(v) => setForm({ ...form, description: v })}
            />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>{t("admin.permissions")}</Label>
            <div className="max-h-64 grid grid-cols-2 gap-1 overflow-y-auto">
              {permOptions.map((option) => {
                const checked = form.permission_ids.includes(option.value);
                return (
                  <label key={option.value} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={checked}
                      onCheckedChange={(v) =>
                        setForm({
                          ...form,
                          permission_ids: v
                            ? [...form.permission_ids, option.value]
                            : form.permission_ids.filter((p) => p !== option.value),
                        })
                      }
                    />
                    {option.label}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}
      onSave={async (mode, form, record) => {
        const payload = {
          code: form.code,
          name: form.name,
          description: form.description || null,
          permission_ids: form.permission_ids,
        };
        return mode === "edit" && record ? updateGroup(record.id, payload) : createGroup(payload);
      }}
      onDelete={(record) => deleteGroup(record.id)}
      recordTitle={(r) => r.code}
      detailFields={(r) => [
        { label: t("admin.code"), value: r.code, field: "code" },
        { label: t("admin.name"), value: r.name, field: "name" },
        { label: t("admin.description"), value: r.description, field: "description" },
        {
          label: t("admin.permissions"),
          value: (permissions.data?.items ?? [])
            .filter((p) => (r.permission_ids ?? []).includes(p.id))
            .map((p) => p.code)
            .join(", "),
        },
      ]}
    />
  );
}
